import { useState, useEffect, Dispatch, SetStateAction } from 'react';
import isObject from 'lodash/isObject';

type BaseInputData = { id: string };
type ListSelection<T = boolean> = Record<string, T>;

// Replaces `boolean` with `TOutputData` in selection state in order to keep some data about selected items
export const useModifiedListSelection = <
  TInputData extends BaseInputData,
  TOutputData extends object,
>(
  items?: TInputData[],
  onFilesSelect?: (items: TOutputData[]) => void,
  transform?: (item: TInputData) => TOutputData,
) => {
  const [selection, setSelection] = useState<
    ListSelection<TOutputData | boolean>
  >({});

  useEffect(() => {
    for (const selectedId in selection) {
      const value = selection[selectedId];
      const item = items?.find(({ id }) => id === selectedId);
      if (item && value && typeof value !== 'object') {
        const selected = (transform ? transform(item) : item) as TOutputData;
        setSelection((prevState) => ({ ...prevState, [selectedId]: selected }));
      }
    }
  }, [selection, items, transform]);

  useEffect(() => {
    onFilesSelect?.(Object.values(selection).filter(isObject) as TOutputData[]);
  }, [selection, onFilesSelect]);

  return [selection, setSelection] as [
    ListSelection,
    Dispatch<SetStateAction<ListSelection>>,
  ];
};
